import React, { useState } from 'react';
import { X, CheckCircle, Circle, Sparkles } from 'lucide-react';
import AIProcessor from './AIProcessor';

const TaskModal = ({ task, onSave, onClose }) => {
    const [formData, setFormData] = useState({
        title: task?.title || '',
        description: task?.description || '',
        priority: task?.priority || 'medium',
        dueDate: task?.dueDate || '',
        tags: task?.tags || [],
        subtasks: task?.subtasks || [],
        status: task?.status || 'todo',
        type: task?.type || 'todo'
    });

    const [tagInput, setTagInput] = useState('');
    const [subtaskInput, setSubtaskInput] = useState('');
    const [showAI, setShowAI] = useState(false);
    const [isAIProcessing, setIsAIProcessing] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            alert('Please enter a task title');
            return;
        }
        if (task) {
            onSave(task.id, formData);
        } else {
            onSave(formData);
        }
    };

    const handleAIImprove = async (rawNote) => {
        setIsAIProcessing(true);
        try {
            // Call AI service (mock for now)
            const aiResult = await mockAIImprove(rawNote || formData.title);

            // Update form with AI suggestions
            setFormData({
                ...formData,
                title: aiResult.title || formData.title,
                description: aiResult.description || formData.description,
                priority: aiResult.priority || formData.priority,
                dueDate: aiResult.dueDate || formData.dueDate,
                subtasks: aiResult.subtasks || formData.subtasks
            });

            setShowAI(false);
        } catch (error) {
            console.error('AI processing failed:', error);
            alert('AI processing failed. Please try again.');
        } finally {
            setIsAIProcessing(false);
        }
    };

    // Mock AI function (replace with real API call)
    const mockAIImprove = (input) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    title: input,
                    description: `This is an AI-generated detailed description for: "${input}". The task includes multiple steps and considerations that should be taken into account for successful completion.`,
                    priority: 'high',
                    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    subtasks: [
                        { title: 'Research and plan approach', completed: false },
                        { title: 'Set up required resources', completed: false },
                        { title: 'Execute main task', completed: false },
                        { title: 'Review and finalize', completed: false }
                    ]
                });
            }, 2000);
        });
    };

    const addTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
    };

    const addSubtask = () => {
        if (subtaskInput.trim()) {
            setFormData({
                ...formData,
                subtasks: [...formData.subtasks, { title: subtaskInput.trim(), completed: false }]
            });
            setSubtaskInput('');
        }
    };

    const removeSubtask = (index) => {
        setFormData({
            ...formData,
            subtasks: formData.subtasks.filter((_, i) => i !== index)
        });
    };

    const toggleSubtask = (index) => {
        const updated = [...formData.subtasks];
        updated[index].completed = !updated[index].completed;
        setFormData({ ...formData, subtasks: updated });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="glass-card max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-poppins font-semibold text-white">
                        {task ? 'Edit Task' : 'Create New Task'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/5 rounded transition-colors"
                    >
                        <X size={24} className="text-white/70" />
                    </button>
                </div>

                {/* AI Processor */}
                {showAI && (
                    <AIProcessor
                        onProcess={handleAIImprove}
                        isProcessing={isAIProcessing}
                        onClose={() => setShowAI(false)}
                    />
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Task Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                            placeholder="Enter task title..."
                            required
                        />
                    </div>

                    {/* AI Button */}
                    <button
                        type="button"
                        onClick={() => setShowAI(true)}
                        className="btn-primary w-full flex items-center justify-center gap-2 ai-pulse"
                    >
                        <Sparkles size={20} />
                        Improve with AI
                    </button>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={4}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors resize-none"
                            placeholder="Add detailed description..."
                        />
                    </div>

                    {/* Task Type */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Task Type
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {['todo', 'habit', 'daily'].map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, type })}
                                    className={`px-4 py-2 rounded-lg border capitalize transition-all ${formData.type === type
                                            ? 'bg-accent border-accent text-white'
                                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                                        }`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Priority & Status */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Priority
                            </label>
                            <select
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-accent transition-colors"
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-accent transition-colors"
                            >
                                <option value="todo">To Do</option>
                                <option value="in-progress">In Progress</option>
                                <option value="done">Done</option>
                            </select>
                        </div>
                    </div>

                    {/* Due Date */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Due Date
                        </label>
                        <input
                            type="date"
                            value={formData.dueDate}
                            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-accent transition-colors"
                        />
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Tags
                        </label>
                        <div className="flex gap-2 mb-2">
                            <input
                                type="text"
                                value={tagInput}
                                onChange={(e) => setTagInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                                placeholder="Add tag..."
                            />
                            <button
                                type="button"
                                onClick={addTag}
                                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors"
                            >
                                Add
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {formData.tags.map((tag, index) => (
                                <span key={index} className="tag-chip flex items-center gap-1">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(tag)}
                                        className="hover:text-red-400"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Subtasks */}
                    <div>
                        <label className="block text-sm font-medium text-white/80 mb-2">
                            Subtasks
                        </label>
                        <div className="flex gap-2 mb-3">
                            <input
                                type="text"
                                value={subtaskInput}
                                onChange={(e) => setSubtaskInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtask())}
                                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-accent transition-colors"
                                placeholder="Add subtask..."
                            />
                            <button
                                type="button"
                                onClick={addSubtask}
                                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-colors"
                            >
                                Add
                            </button>
                        </div>
                        <div className="space-y-2">
                            {formData.subtasks.map((subtask, index) => (
                                <div key={index} className="flex items-center gap-2 slide-in">
                                    <button
                                        type="button"
                                        onClick={() => toggleSubtask(index)}
                                        className="text-accent hover:text-accent/80"
                                    >
                                        {subtask.completed ? <CheckCircle size={18} /> : <Circle size={18} />}
                                    </button>
                                    <span className={`flex-1 text-sm ${subtask.completed ? 'line-through opacity-50' : ''}`}>
                                        {subtask.title}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeSubtask(index)}
                                        className="text-white/40 hover:text-red-400 text-lg"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 btn-primary"
                        >
                            {task ? 'Update Task' : 'Create Task'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModal;
